import React, { useState } from "react";
import HarvestApplicationsGridRow from "./HarvestApplicationsGridRow";
import { IHarvestApplicationResponseModel } from "../../../typings/models/HarvestApplicationResponseModel";
import "../../../styles/gridStyle.css";
import Loader from "../../../components/molecules/Loader";

interface GridProps {
  data: IHarvestApplicationResponseModel[];
  isLoading: boolean;
}

const HarvestApplicationsGrid: React.FC<GridProps> = ({ data, isLoading }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;

  // Filter the data based on the search query
  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
    )
  );

  // Function to perform sorting based on the current sort settings
  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get the data to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If a new column is clicked, set the new column and default to ascending sort
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };
  return (
    <div className="container">
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="form-control mb-3 search-input"
      />

      <table className="table table-bordered table-hover">
        {/* Table header */}
        <thead>
          <tr>
            <th
              className="grid-header"
              onClick={() => handleSort("requestReferenceNumber")}
            >
              Application Number{" "}
              {sortColumn === "requestReferenceNumber" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("brooderMicrochipNumber")}
            >
              Brooder Microchip Number{" "}
              {sortColumn === "brooderMicrochipNumber" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("batchNumber")}
            >
              Batch Number{" "}
              {sortColumn === "batchNumber" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("uen")}>
              UEN No.{" "}
              {sortColumn === "uen" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("companyName")}
            >
              Company Name{" "}
              {sortColumn === "companyName" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("harvestDate")}
            >
              Harvest Date{" "}
              {sortColumn === "harvestDate" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("mobileNo")}>
              Mobile No.{" "}
              {sortColumn === "mobileNo" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header" onClick={() => handleSort("email")}>
              Email{" "}
              {sortColumn === "email" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th
              className="grid-header"
              onClick={() => handleSort("requestSubmittedDate")}
            >
              Submitted Date{" "}
              {sortColumn === "requestSubmittedDate" && (
                <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
              )}
            </th>
            <th className="grid-header">Action</th>
            <th className="hidden"></th>
            {/* Add other header columns here */}
          </tr>
        </thead>
        <tbody>
          {dataToShow.map((item) => (
            <HarvestApplicationsGridRow
              key={item.harvestDetailsApplicationId}
              data={item}
            />
          ))}
        </tbody>
      </table>
      {isLoading && <Loader />}

      {/* Pagination */}
      <nav aria-label="Page navigation example">
        <ul className="pagination">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage - 1)}
            >
              Previous
            </button>
          </li>
          {renderPageButtons()}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default HarvestApplicationsGrid;
